"use client";

import Link from "next/link";
import { useState, useEffect, ReactNode } from "react";
import {
    CheckCircleIcon, FacebookIcon, InstagramIcon, MailIcon, ShieldIcon, TwitterIcon, UsersIcon,
    ChevronDownIcon ,PinterestIcon
} from "@/utils/icons";
import { apiFetch } from "@/utils/api";
import { DeviceInfo, getDeviceInfo } from "@/utils/devices";
import ScrollToTopButton from "../ui/ScrollToTopButton";
import Image from "next/image";


const companyLinks = [
    { name: "About Us", href: "/about-us/" },
    { name: "FAQs", href: "/faqs/" },
    { name: "Privacy Statement", href: "/privacy/" },
    { name: "Terms of Service", href: "/terms-of-service/" },
    { name: "Contact Us", href: "/contact-us/" },
    //   { name: "Careers", href: "/careers/" },
];

const topPages = [
    { name: "Top 50 Coupons", href: "/store/" },
    { name: "Deals Of The Day", href: "/offers/" },
    { name: "Trending Categories", href: "/category/" },
    // { name: "Bank Offers", href: "/bank-wallet-offers/" },
    //   { name: "Max Cashback", href: "/cashback/" },
    //   { name: "Read Best", href: "/blogs/" },
];

// const countries = [
//   { name: "India", href: "#", flag: "🇮🇳" },
//   { name: "USA", href: "#", flag: "🇺🇸" },
//   { name: "UAE", href: "#", flag: "🇦🇪" },
//   { name: "Singapore", href: "#", flag: "🇸🇬" },
// ];

const socialLinks = [
    { name: "Facebook", href: "https://www.facebook.com/offercounty/", icon: FacebookIcon },
    { name: "Twitter", href: "https://twitter.com/offercounty/", icon: TwitterIcon },
    { name: "Instagram", href: "https://www.instagram.com/offercountymedia/", icon: InstagramIcon },
    { name: "Pinterest", href: "https://www.pinterest.co.uk/offercounty/", icon: PinterestIcon },
    // { name: "YouTube", href: "https://youtube.com/dealsncoupon/", icon: YoutubeIcon },
];

interface AccordionItemProps {
    title: string;
    children: ReactNode;
    isOpen: boolean;
    onClick: () => void;
}

// Reusable Accordion Item Component
const AccordionItem: React.FC<AccordionItemProps> = ({ title, children, isOpen, onClick }) => {
    return (
        <div className="border-b border-gray-600">
            <button
                className="flex justify-between items-center w-full py-4 text-left focus:outline-none"
                onClick={onClick}
            >
                <h3 className="text-sm font-bold uppercase text-slate-400">{title}</h3>
                <ChevronDownIcon
                    className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""
                        }`}
                />
            </button>
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-[800px] opacity-100 pb-4" : "max-h-0 opacity-0"
                    }`}
                style={{
                    maxHeight: isOpen ? "800px" : "0",
                    paddingBottom: isOpen ? "1rem" : "0",
                }}
            >
                {children}
            </div>
        </div>
    );
};

// DESKTOP VIEW COMPONENT
const DesktopFooter = ({
    loadingStores, errorStores, stores, loadingCategories, errorCategories, categories
}: {
    handleDownloadApp: () => void;
    loadingStores: boolean;
    errorStores: string | null;
    stores: { _id?: string; name: string;slug: string;  }[] | null;
    loadingCategories: boolean;
    errorCategories: string | null;
    categories: { _id?: string; name: string; slug: string; }[] | null;
}) => (
    <>
        <div className="py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                {/* Company section */}
                <div className="lg:col-span-1">
                    <h3 className="text-slate-400 font-semibold text-sm mb-4 uppercase tracking-wide">
                        Company
                    </h3>
                    <ul className="space-y-2">
                        {companyLinks.map((link) => (
                            <li key={link.name}>
                                <Link
                                    href={link.href+"/"}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {link.name}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Contact info */}
                    <div className="mt-6 space-y-2">
                        <div className="flex items-center space-x-2 text-sm text-slate-200">
                            <MailIcon className="h-4 w-4" />
                            <span>{process.env.NEXT_PUBLIC_APP_EMAIL}</span>
                        </div>
                        {/* <div className="flex items-center space-x-2 text-sm text-slate-200">
                    <PhoneIcon className="h-4 w-4" />
                    <span>{process.env.NEXT_PUBLIC_APP_PHONE}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-slate-200">
                    <MapPinIcon className="h-4 w-4" />
                    <span>{process.env.NEXT_PUBLIC_APP_LOCATION}</span>
                    </div> */}
                    </div>
                </div>

                {/* Top Pages section */}
                <div className="lg:col-span-1">
                    <h3 className="text-slate-400 font-semibold text-sm mb-4 uppercase tracking-wide">
                        Top Pages
                    </h3>
                    <ul className="space-y-2">
                        {topPages.map((link) => (
                            <li key={link.name}>
                                <Link
                                    href={link.href+"/"}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {link.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Stores section */}
                <div className="lg:col-span-1">
                    <h3 className="text-slate-400 font-semibold text-sm mb-4 uppercase tracking-wide">
                        Stores
                    </h3>
                    <ul className="space-y-2">
                        {loadingStores && Array.from({ length: 8 }).map((_, i) => (
                            <li key={i} className="animate-pulse bg-gray-600 rounded h-4 w-24" />
                        ))}
                        {errorStores && !loadingStores && (
                            <li className="text-red-400">{errorStores}</li>
                        )}
                        {!loadingStores && stores && stores.length === 0 && !errorStores && (
                            <li className="text-slate-400">No stores found.</li>
                        )}
                        {!loadingStores && stores && stores.slice(0, 6).map((store) => (
                            <li key={store._id || store.name}>
                                <Link
                                    href={`/store/${store.slug}/`}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {store.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Categories section */}
                <div className="lg:col-span-1">
                    <h3 className="text-slate-400 font-semibold text-sm mb-4 uppercase tracking-wide">
                        Categories
                    </h3>
                    <ul className="space-y-2">
                        {loadingCategories && Array.from({ length: 8 }).map((_, i) => (
                            <li key={i} className="animate-pulse bg-gray-600 rounded h-4 w-24" />
                        ))}
                        {errorCategories && !loadingCategories && (
                            <li className="text-red-400">{errorCategories}</li>
                        )}
                        {!loadingCategories && categories && categories.length === 0 && !errorCategories && (
                            <li className="text-slate-400">No categories found.</li>
                        )}
                        {!loadingCategories && categories && categories.slice(0, 6).map((category) => (
                            <li key={category._id || category.name}>
                                <Link
                                    href={`/category/${category.slug}/`}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {category.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Policy links */}
                <div className="lg:col-span-1">
                    <h3 className="text-slate-400 font-semibold text-sm mb-4 uppercase tracking-wide">
                        For General
                    </h3>
                    <ul className="space-y-2">
                        <li>
                            <Link href="/privacy-policy/" className="hover:text-primary-600 transition-colors">
                                Privacy Policy
                            </Link>
                        </li>
                        <li>
                            <Link href="/terms-of-use/" className="hover:text-primary-600 transition-colors">
                                Terms of Use
                            </Link>
                        </li>
                        <li>
                            <Link href="/cookie-policy/" className="hover:text-primary-600 transition-colors">
                                Cookie Policy
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* Countries section */}
                {/* <div className="lg:col-span-1">
                <h3 className="text-slate-400 font-semibold text-sm mb-4 uppercase tracking-wide">
                    Countries
                </h3>
                <ul className="space-y-2">
                    {countries.map((country) => (
                    <li key={country.name}>
                        <Link
                        href={country.href}
                        className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200 flex items-center space-x-2"
                        >
                        <span>{country.flag}</span>
                        <span>{country.name}</span>
                        </Link>
                    </li>
                    ))}
                </ul>
                </div> */}

                {/* Download App section  */}
                {/* <div className="lg:col-span-1">
                    <h3 className="text-slate-400 font-semibold text-sm mb-4 uppercase tracking-wide">
                        Download Our App
                    </h3>
                    <button
                        onClick={handleDownloadApp}
                        className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center space-x-2 group"
                    >
                        <DownloadIcon className="h-5 w-5 group-hover:animate-bounce" />
                        <span className="font-medium">Download App</span>
                    </button>
                    
                    <div className="mt-4 text-xs text-slate-500">
                        <p>Get exclusive offers and deals on your mobile device</p>
                    </div>
                </div> */}
            </div>
        </div>

        {/* Logo and description section - Original */}
        < div className="border-t border-gray-600 py-8" >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
                <div className="flex items-center space-x-4 mb-4 md:mb-0">
                {process.env.NEXT_PUBLIC_APP_TITLE === "OfferCounty" ? 

                    <div className="flex items-center space-x-2">
                        <div className="relative">
                            <Image
                                src="/favicon.ico"
                                alt="Logo"
                                width={40}
                                height={40}
                                className="w-10 h-10 object-contain"
                            />
                        </div>
                        <div>
                            <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-primary-400 bg-clip-text text-transparent">
                                {process.env.NEXT_PUBLIC_APP_TITLE}
                            </span>
                            <div className="text-xs text-gray-400">{process.env.NEXT_PUBLIC_APP_SLOGAN}</div>
                        </div>
                    </div>
                    :
                    <div className="flex items-center space-x-2">

                    {/* <div className="relative">
                      <TicketIcon className="rotate-90 w-8 h-8 text-purple-500" />
                    </div> */}
                    <div>
                      {/* <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-primary-400 bg-clip-text text-transparent">
                        {process.env.NEXT_PUBLIC_APP_TITLE}
                      </span> */}
                      <div className="flex items-end">

                      <Image src="/logo_light2.ico" alt="Logo" className="object-contain" width={130} height={50} />
                      <div className="text-primary-500 text-[17px] font-extralight place-self-end">tweak</div>
                      </div>

                      <div className="text-xs text-center text-gray-400 mt-1">
                        {process.env.NEXT_PUBLIC_APP_SLOGAN}
                      </div>
                    </div>
                  </div>
                  }
                    <div>
                        <p className="text-slate-500 font-medium">India&apos;s Leading Coupons & Deals Marketplace</p>
                        <p className="text-slate-500 text-sm mt-1">
                            Save money on your favorite brands and stores
                        </p>
                    </div>
                </div>

                {/* Social media links */}
                <div className="flex items-center space-x-4">
                    <span className="text-slate-200 text-sm font-medium">Follow us:</span>
                    {socialLinks.map((social) => {
                        const Icon = social.icon;
                        return (
                            <a
                                key={social.name}
                                href={social.href+"/"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-primary-600 transition-colors duration-200"
                                aria-label={`Follow us on ${social.name}`}
                            >
                                <Icon className="h-5 w-5" />
                            </a>
                        );
                    })}
                </div>
            </div>
        </div >

        {/* Trust indicators - Original */}
        < div className="border-t border-gray-600 py-6" >
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                <div className="flex items-center space-x-8">
                    <div className="flex items-center space-x-2">
                        <ShieldIcon className="h-5 w-5 text-primary-500" />
                        <span className="text-sm text-slate-200">SSL Secured</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <CheckCircleIcon className="h-5 w-5 text-primary-500" />
                        <span className="text-sm text-slate-200">100% Safe & Secure</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <UsersIcon className="h-5 w-5 text-primary-500" />
                        <span className="text-sm text-slate-200">Trusted by 3.5M+ Users</span>
                    </div>
                </div>
            </div>
        </div >
    </>
);

// MOBILE VIEW COMPONENT

const MobileFooter = ({
    stores, loadingStores, errorStores,
    categories, loadingCategories, errorCategories
}: {
    handleDownloadApp: () => void;
    stores: { _id?: string; name: string; slug: string;}[] | null;
    loadingStores: boolean;
    errorStores: string | null;
    categories: { _id?: string; name: string; slug: string; }[] | null;
    loadingCategories: boolean;
    errorCategories: string | null;
}) => {
    const [openSection, setOpenSection] = useState<string | null>(null);

    const toggleSection = (sectionName: string) => {
        setOpenSection(openSection === sectionName ? null : sectionName);
    };

    return (
        <div className="py-8">
            {/* LOGO and DESCRIPTION section for Mobile (STATIC) */}
            <div className="pb-8 border-b border-gray-600">
            {process.env.NEXT_PUBLIC_APP_TITLE === "OfferCounty" ? 

                <div className="flex items-center space-x-2 mb-4">
                    <div className="relative">
                        <Image
                            src="/favicon.ico"
                            alt="Logo"
                            width={40}
                            height={40}
                            className="w-10 h-10 object-contain"
                        />
                    </div>
                    <div>
                        <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-primary-400 bg-clip-text text-transparent">
                            {process.env.NEXT_PUBLIC_APP_TITLE}
                        </span>
                        <div className="text-xs text-gray-400">{process.env.NEXT_PUBLIC_APP_SLOGAN}</div>
                    </div>
                </div>
                :
                <div className="flex items-center space-x-2 mb-4">

                {/* <div className="relative">
                  <TicketIcon className="rotate-90 w-8 h-8 text-purple-500" />
                </div> */}
                <div>
                  {/* <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-primary-400 bg-clip-text text-transparent">
                    {process.env.NEXT_PUBLIC_APP_TITLE}
                  </span> */}
                  <div className="flex items-end">

                  <Image src="/logo_light2.ico" alt="Logo" className="object-contain" width={150} height={50} />
                      <div className="text-primary-500 text-[17px] font-extralight place-self-end">tweak</div>
                  </div>

                  <div className="text-xs text-center mt-1 text-gray-400">
                    {process.env.NEXT_PUBLIC_APP_SLOGAN}
                  </div>
                </div>
              </div>
              }
                <p className="font-medium">India&apos;s Leading Coupons & Deals Marketplace</p>
                <p className="text-sm mt-1 text-slate-400">
                    Save money on your favorite brands and stores
                </p>
            </div>

            {/* ACCORDION SECTIONS  */}
            <div className="pt-2">
                {/* 1. COMPANY Section */}
                <AccordionItem
                    title="Company"
                    isOpen={openSection === "company"}
                    onClick={() => toggleSection("company")}
                >
                    <ul className="space-y-3">
                        {companyLinks.map((link) => (
                            <li key={link.name}>
                                <Link
                                    href={link.href+"/"}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {link.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </AccordionItem>

                {/* 2. TOP PAGES Section */}
                <AccordionItem
                    title="Top Pages"
                    isOpen={openSection === "topPages"}
                    onClick={() => toggleSection("topPages")}
                >
                    <ul className="space-y-3">
                        {topPages.map((link) => (
                            <li key={link.name}>
                                <Link
                                    href={link.href+"/"}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {link.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </AccordionItem>

                {/* 3. STORES Section */}
                <AccordionItem
                    title="Stores"
                    isOpen={openSection === "stores"}
                    onClick={() => toggleSection("stores")}
                >
                    <ul className="space-y-3">
                        {loadingStores && Array.from({ length: 6 }).map((_, i) => (
                            <li key={i} className="animate-pulse bg-gray-600 rounded h-4 w-24" />
                        ))}
                        {errorStores && !loadingStores && (
                            <li className="text-red-400 text-sm">{errorStores}</li>
                        )}
                        {!loadingStores && stores && stores.length === 0 && !errorStores && (
                            <li className="text-slate-400 text-sm">No stores found.</li>
                        )}
                        {!loadingStores && stores && stores.slice(0, 8).map((store) => (
                            <li key={store._id || store.name}>
                                <Link
                                    href={`/store/${store.slug}/`}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {store.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </AccordionItem>

                {/* 4. CATEGORIES Section */}
                <AccordionItem
                    title="Categories"
                    isOpen={openSection === "categories"}
                    onClick={() => toggleSection("categories")}
                >
                    <ul className="space-y-3">
                        {loadingCategories && Array.from({ length: 6 }).map((_, i) => (
                            <li key={i} className="animate-pulse bg-gray-600 rounded h-4 w-24" />
                        ))}
                        {errorCategories && !loadingCategories && (
                            <li className="text-red-400 text-sm">{errorCategories}</li>
                        )}
                        {!loadingCategories && categories && categories.length === 0 && !errorCategories && (
                            <li className="text-slate-400 text-sm">No categories found.</li>
                        )}
                        {!loadingCategories && categories && categories.slice(0, 8).map((category) => (
                            <li key={category._id || category.name}>
                                <Link
                                    href={`/category/${category.slug}/`}
                                    className="text-slate-200 hover:text-primary-600 text-sm transition-colors duration-200"
                                >
                                    {category.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </AccordionItem>

                {/* 5. CONTACT US Section */}
                <AccordionItem
                    title="Contact Us"
                    isOpen={openSection === "contact_us"}
                    onClick={() => toggleSection("contact_us")}
                >
                    <div className="space-y-4">
                        <div className="flex flex-col space-y-3 text-sm">
                            <div className="flex items-start space-x-2 text-slate-200">
                                <MailIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-primary-400" />
                                <a href={`mailto:${process.env.NEXT_PUBLIC_APP_EMAIL}`} className="break-all hover:text-white">{process.env.NEXT_PUBLIC_APP_EMAIL}</a>
                            </div>
                            {/* <div className="flex items-start space-x-2 text-slate-200">
                                <PhoneIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-primary-400" />
                                <a href={`tel:${process.env.NEXT_PUBLIC_APP_PHONE}`} className="hover:text-white">{process.env.NEXT_PUBLIC_APP_PHONE}</a>
                            </div>
                            <div className="flex items-start space-x-2 text-slate-200">
                                <MapPinIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-primary-400" />
                                <span className="break-words">{process.env.NEXT_PUBLIC_APP_LOCATION}</span>
                            </div> */}
                        </div>
                    </div>
                </AccordionItem>

                {/* 6. POLICIES Section */}
                <AccordionItem
                    title="Policies"
                    isOpen={openSection === "app_policies"}
                    onClick={() => toggleSection("app_policies")}
                >
                    <div className="space-y-6">
                        {/* POLICY LINKS */}
                        <div>
                            <div className="flex flex-col space-y-3 text-sm">
                                <Link href="/privacy-policy/" className="text-slate-200 hover:text-primary-600 transition-colors">Privacy Policy</Link>
                                <Link href="/terms-of-use/" className="text-slate-200 hover:text-primary-600 transition-colors">Terms of Use</Link>
                                <Link href="/cookie-policy/" className="text-slate-200 hover:text-primary-600 transition-colors">Cookie Policy</Link>
                            </div>
                        </div>
                    </div>
                </AccordionItem>
            </div>

            {/* <div className="py-4 border-t border-gray-600">
                <div className="flex items-center justify-between space-x-2">
                    {countries.map((country) => (
                        <Link
                            key={country.name}
                            href={country.href}
                            className="flex items-center space-x-1 text-xs text-slate-200 hover:text-primary-400 transition-colors duration-200 flex-1 justify-center border-r border-gray-600 last:border-r-0 px-1"
                        >
                            <span>{country.flag}</span>
                            <span className="font-medium">{country.name}</span>
                        </Link>
                    ))}
                </div>
            </div> */}

            {/* TRUST & SECURITY SECTION */}
            <div className="py-4 border-t border-gray-600">
                <div className="flex items-center justify-between space-x-2">
                    <div className="flex items-center space-x-1 justify-center flex-1">
                        <ShieldIcon className="h-4 w-4 text-primary-500" />
                        <span className="text-xs text-slate-200">SSL Secured</span>
                    </div>
                    <div className="h-5 w-px bg-gray-600"></div>
                    <div className="flex items-center space-x-1 justify-center flex-1">
                        <CheckCircleIcon className="h-4 w-4 text-primary-500" />
                        <span className="text-xs text-slate-200">100% Secure</span>
                    </div>
                    <div className="h-5 w-px bg-gray-600"></div>
                    <div className="flex items-center space-x-1 justify-center flex-1">
                        <UsersIcon className="h-4 w-4 text-primary-500" />
                        <span className="text-xs text-slate-200">3.5M+ Users</span>
                    </div>
                </div>
            </div>

            {/* DOWNLOAD APP SECTION  */}
            {/* <div className="pt-4 border-t border-gray-600">
                <div className="flex items-center justify-between">
                    <span className="text-slate-400 font-semibold text-sm uppercase tracking-wide flex-shrink-0">
                        Download Our App
                    </span>
                    <button
                        onClick={handleDownloadApp}
                        className="bg-primary-500 text-white py-2 px-3 rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center space-x-1 group flex-shrink-0"
                    >
                        <DownloadIcon className="h-4 w-4 group-hover:animate-bounce" />
                        <span className="font-medium text-sm">Download</span>
                    </button>
                </div>

                <div className="mt-2 text-xs text-center text-slate-500">
                    <p>Get exclusive offers and deals on your mobile device</p>
                </div>
            </div> */}

            {/* SOCIAL LINKS SECTION  */}
            <div className="pt-3 border-t border-gray-600 mt-3">
                <div className="flex justify-center">
                    <span className="text-slate-200 text-sm font-medium mb-1">Follow us:</span>
                    <div className="px-2 flex space-x-4">
                        {socialLinks.map((social) => {
                            const Icon = social.icon;
                            return (
                                <a
                                    key={social.name}
                                    href={social.href+"/"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-slate-400 hover:text-primary-600 transition-colors duration-200"
                                    aria-label={`Follow us on ${social.name}`}
                                >
                                    <Icon className="h-5 w-5" />
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

// MAIN FOOTER COMPONENT
export default function Footer() {
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
        isMobile: false,
        isIOS: false,
        isAndroid: false,
        isDesktop: true,
    });

    // Data fetching states are now centralized here, and passed to both views
    const [stores, setStores] = useState<{ _id?: string; name: string; slug: string;}[] | null>(null);
    const [categories, setCategories] = useState<{ _id?: string; name: string; slug: string; }[] | null>(null);
    const [loadingStores, setLoadingStores] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [errorStores, setErrorStores] = useState<string | null>(null);
    const [errorCategories, setErrorCategories] = useState<string | null>(null);

    useEffect(() => {
        setDeviceInfo(getDeviceInfo());

        // Fetch Stores
        setLoadingStores(true);
        apiFetch<{ success: boolean; data: { _id?: string; name: string; slug: string;}[] }>("/stores")
            .then((res) => {
                setStores(res.data);
                setErrorStores(null);
            })
            .catch((err) => {
                setErrorStores(err.message || "Failed to load stores");
                setStores([]);
            })
            .finally(() => setLoadingStores(false));

        // Fetch Categories
        setLoadingCategories(true);
        apiFetch<{ success: boolean; data: { _id?: string; name: string; slug: string; }[] }>("/categories")
            .then((res) => {
                setCategories(res.data);
                setErrorCategories(null);
            })
            .catch((err) => {
                setErrorCategories(err.message || "Failed to load categories");
                setCategories([]);
            })
            .finally(() => setLoadingCategories(false));

        return () => { };
    }, []);

    const handleDownloadApp = () => {
        if (deviceInfo.isIOS) {
            window.open("https://apps.apple.com/app/dealsncoupon", "_blank");
        } else if (deviceInfo.isAndroid) {
            window.open("https://play.google.com/store/apps/details?id=com.dealsncoupon", "_blank");
        } else {
            // Show QR code or app store options
            alert("Download our mobile app from App Store or Google Play Store");
        }
    };

    const commonProps = {
        handleDownloadApp,
        loadingStores,
        errorStores,
        stores,
        loadingCategories,
        errorCategories,
        categories,
    };

    return (
        <footer className="bg-gray-700 text-white border-t border-gray-600 relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* DESKTOP VIEW*/}
                <div className="hidden lg:block">
                    <DesktopFooter
                        {...commonProps}
                    />
                </div>

                {/* MOBILE VIEW */}
                <div className="lg:hidden">
                    <MobileFooter {...commonProps} />
                </div>

                <div className="pb-4">
                    <div className="flex flex-col md:flex-row items-center justify-center text-xs text-slate-500">
                        <p>© {new Date().getFullYear()} {process.env.NEXT_PUBLIC_APP_TITLE}. All rights reserved. Made with ❤️ in India</p>
                    </div>
                </div>
            </div>

            <ScrollToTopButton />
        </footer>
    );
}